import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const sigma: Competitor = {
  "id": "sigma",
  "name": "Sigma Computing",
  "group": "bi",
  "website": "https://www.sigmacomputing.com",
  "owner": "Sigma Computing, Inc. (private, venture-backed; $80M Series E at a $3B valuation, May 2026)",
  "summary": "Cloud BI and data-app platform that runs queries live against a cloud warehouse (Snowflake, Databricks, BigQuery, Redshift and others) through a spreadsheet-style interface, with write-back input tables, Python/SQL and AI agents. Built for companies that already keep their data in a cloud warehouse and want business users to work on it without extracts.",
  "bestFor": [
    "Teams on Snowflake or Databricks that want analysis to run live in the warehouse without extracts",
    "Finance and operations users who think in spreadsheets and need to work with very large tables",
    "Planning, forecasting and data-entry workflows that need write-back to the warehouse (input tables)",
    "Embedding governed analytics in a customer portal using JWT-signed secure embeds"
  ],
  "notFor": [
    "Organizations whose data is not in a supported cloud warehouse or database (Sigma has no in-memory extract engine of its own)",
    "Teams that need a free or self-hosted BI tool (no free tier, no self-hosted option found)",
    "Engineering-led teams that want native git branching and pull-request review of BI content (as of late 2026, version control is via version tags and an API, not a native git integration)",
    "Buyers who need a published per-seat price before talking to sales"
  ],
  "strengths": [
    "All queries run in the customer's warehouse, so there are no extracts to schedule or refresh and data stays in the warehouse",
    "Spreadsheet grid with 200+ functions on live warehouse data, so Excel users can work without learning SQL or a modeling language",
    "Input tables let users enter or edit data that is stored in the warehouse, which most BI tools do not offer natively",
    "Python and SQL in the same workbook, with Python running on Snowflake or Databricks compute",
    "Fast-moving AI roadmap in 2025-2026: Sigma Assistant, Sigma Agents, AI columns, an MCP server, and links to Snowflake Cortex Agents and Databricks Genie",
    "Broad scheduled export options: email, Slack, Teams, SharePoint, Google Sheets/Drive, webhooks and cloud storage"
  ],
  "limitations": [
    "No public pricing; every quote goes through sales. Buyer-reported data (Vendr) shows a median contract around $65K per year",
    "No free tier; the self-serve trial lasts 7 days",
    "No native git integration: version tags plus REST/spec APIs only, so teams have to build their own CI/CD. The workbooks-as-code API was described as private beta in September 2026, and the data-model spec API does not support input tables or Python elements",
    "Python only works on Snowflake and Databricks connections, and has limits (for example, Python output cannot be materialized, and column-level security is not applied inside Python code)",
    "Input tables (write-back) are not supported on several sources, including MySQL, AlloyDB, Azure SQL Database, SQL Server, Starburst Galaxy, ClickHouse and MotherDuck",
    "Performance and warehouse spend follow the underlying warehouse; reviewers report slow loads on large or many-source reports",
    "Exports are capped at 1 million rows (CSV/Excel) and PDF tables at 1,000 rows; cloud-storage exports are exempt from the row cap",
    "Reviewers mention a learning curve of a couple of weeks and fewer visualization options than older tools (for example, limited map types)",
    "SaaS only (AWS, Azure or GCP regions); no self-hosted or on-premises deployment"
  ],
  "pricing": {
    "model": "Annual subscription: platform tier plus per-user licenses (Creator / Explorer / Viewer, per third-party buyer data)",
    "publicStartingPrice": "not published",
    "freeTier": "no; 7-day free trial with sample data or your own warehouse",
    "notes": "Sigma's pricing page shows no prices and sends buyers to sales. Vendr buyer data (third-party, directional only) lists Essential, Business and Enterprise tiers, Creator licenses at roughly $1,200-$5,000+ per year depending on tier, and a median contract of about $65K per year (range ~$15.6K-$145K). Warehouse compute is billed separately by your warehouse vendor.",
    "sourceUrl": "https://www.sigmacomputing.com/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "Workbooks query the connected warehouse directly; no extract engine.",
      "sourceUrl": "https://www.sigmacomputing.com/product/spreadsheets"
    },
    "spreadsheet_interface": {
      "value": "yes",
      "note": "Spreadsheet grid with 200+ functions on live warehouse data.",
      "sourceUrl": "https://www.sigmacomputing.com/product/spreadsheets"
    },
    "sql_editor": {
      "value": "yes",
      "note": "Custom SQL elements in workbooks and data models.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/write-and-run-python-code"
    },
    "python_support": {
      "value": "partial",
      "note": "Only on Snowflake and Databricks connections; output cannot be materialized; only JWT-signed embeds are supported.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/write-and-run-python-code",
      "badge": "Snowflake & Databricks only"
    },
    "writeback": {
      "value": "yes",
      "note": "Input tables write to a warehouse write schema. Not available on some sources (MySQL, SQL Server, Azure SQL DB, AlloyDB, Starburst, ClickHouse, MotherDuck).",
      "sourceUrl": "https://help.sigmacomputing.com/docs/input-tables"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Data models with reusable metrics, relationships and RLS, also manageable as JSON/YAML via the API.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/manage-data-models-as-code/llms.txt"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "JWT-signed secure embeds that pass user attributes and teams for RLS.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/row-level-security-in-embeds"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Sigma Assistant answers natural-language questions and shows its SQL; requires an AI provider set up for the org. Some AI surfaces, such as homepage chat, are still beta.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/ask-natural-language-queries-with-assistant"
    },
    "version_control_git": {
      "value": "partial",
      "note": "Version tags (with protected-tag approvals) and spec APIs; no built-in git sync, so teams build their own pipeline.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/add-version-tags-to-workbooks-and-data-models",
      "badge": "Version tags + API"
    },
    "open_source_or_self_host": {
      "value": "no",
      "note": "SaaS only, hosted on AWS, Azure or GCP regions.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/region-warehouse-and-feature-support"
    },
    "desktop_authoring_required": {
      "value": "no",
      "note": "Authoring is entirely in the browser.",
      "sourceUrl": "https://www.sigmacomputing.com/product/spreadsheets"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Email, Slack, Teams, SharePoint, Google Sheets/Drive, webhook and cloud storage.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/send-or-schedule-workbook-exports"
    },
    "row_level_security": {
      "value": "yes",
      "note": "Based on user, team or user attribute; works in embeds via JWT claims.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/row-level-security-in-embeds"
    },
    "excel_export": {
      "value": "yes",
      "note": "Excel and CSV up to 1M rows; larger exports go to cloud storage.",
      "sourceUrl": "https://help.sigmacomputing.com/docs/download-export-and-upload-limitations"
    }
  },
  "recentChanges": [
    {
      "date": "2025-03",
      "change": "Launched Data Apps for building operational apps on warehouse data.",
      "sourceUrl": "https://www.businesswire.com/news/home/20250313240548/en/Sigma-Unveils-Data-Apps-to-Unify-Business-Workflows-Directly-on-the-Cloud-Data-Platform"
    },
    {
      "date": "2025-09",
      "change": "Announced a set of new AI analytics features.",
      "sourceUrl": "https://www.businesswire.com/news/home/20250910617445/en/Sigma-Reveals-New-AI-BI-and-Analytics-Features-Redefining-Data-Exploration-Capabilities-for-Customers"
    },
    {
      "date": "2026-03",
      "change": "First user conference, Workflow 2026, in San Francisco. Previewed conversational and autonomous Sigma Agents, a Sigma MCP Server, natural language to data models, scheduled actions and expanded API connectors.",
      "sourceUrl": "https://sigmacomputing.com/blog/workflow-2026-insights-from-sigmas-first-user-conference"
    },
    {
      "date": "2026-04",
      "change": "Reported $200M+ ARR, roughly double the prior year, and 2,000+ customers.",
      "sourceUrl": "https://am590theanswer.com/technology/sigma-announces-200m-arr-as-enterprises-abandon-legacy-bi-for-ai-native-analytic/6550a3d709174c73af94b2e20c5f7ea7"
    },
    {
      "date": "2026-05",
      "change": "Raised an $80M Series E at a $3B valuation led by Princeville Capital, with Databricks, ServiceNow and Workday venture arms joining.",
      "sourceUrl": "https://www.businesswire.com/news/home/20260518790028/en/"
    },
    {
      "date": "2026-06",
      "change": "Released AI columns, MCP tools for Sigma Agents, warehouse agents (Snowflake Cortex Agents, Databricks Genie) and an AI usage dashboard; hierarchy columns reached GA (per InterWorks).",
      "sourceUrl": "https://interworks.com/blog/2026/06/29/my-favorite-new-sigma-features-for-june-2026/"
    },
    {
      "date": "2026-06",
      "change": "Named 2026 Business Intelligence Snowflake Product Partner of the Year.",
      "sourceUrl": "https://www.businesswire.com/news/home/20260602857603/en/Sigma-Named-2026-Business-Intelligence-Snowflake-Product-Partner-of-the"
    }
  ],
  "sources": [
    {
      "title": "Sigma pricing page (no prices listed)",
      "url": "https://www.sigmacomputing.com/pricing"
    },
    {
      "title": "Sigma spreadsheets product page",
      "url": "https://www.sigmacomputing.com/product/spreadsheets"
    },
    {
      "title": "Sigma docs: Region, warehouse and feature support",
      "url": "https://help.sigmacomputing.com/docs/region-warehouse-and-feature-support"
    },
    {
      "title": "Sigma docs: Write and run Python code",
      "url": "https://help.sigmacomputing.com/docs/write-and-run-python-code"
    },
    {
      "title": "Sigma docs: Input tables overview",
      "url": "https://help.sigmacomputing.com/docs/input-tables"
    },
    {
      "title": "Sigma docs: Sigma Assistant",
      "url": "https://help.sigmacomputing.com/docs/ask-natural-language-queries-with-assistant"
    },
    {
      "title": "Sigma docs: Version tags",
      "url": "https://help.sigmacomputing.com/docs/add-version-tags-to-workbooks-and-data-models"
    },
    {
      "title": "Sigma docs: Manage data models as code",
      "url": "https://help.sigmacomputing.com/docs/manage-data-models-as-code/llms.txt"
    },
    {
      "title": "Sigma docs: RLS in embeds",
      "url": "https://help.sigmacomputing.com/docs/row-level-security-in-embeds"
    },
    {
      "title": "Sigma docs: Send or schedule workbook exports",
      "url": "https://help.sigmacomputing.com/docs/send-or-schedule-workbook-exports"
    },
    {
      "title": "Sigma docs: Download, export and upload limitations",
      "url": "https://help.sigmacomputing.com/docs/download-export-and-upload-limitations"
    },
    {
      "title": "Sigma docs: Start your Sigma trial",
      "url": "https://help.sigmacomputing.com/docs/start-your-sigma-trial"
    },
    {
      "title": "Sigma blog: Workflow 2026 recap",
      "url": "https://sigmacomputing.com/blog/workflow-2026-insights-from-sigmas-first-user-conference"
    },
    {
      "title": "Business Wire: Sigma Series E (May 2026)",
      "url": "https://www.businesswire.com/news/home/20260518790028/en/"
    },
    {
      "title": "InterWorks: New Sigma features, June 2026 (third party)",
      "url": "https://interworks.com/blog/2026/06/29/my-favorite-new-sigma-features-for-june-2026/"
    },
    {
      "title": "phData: Sigma API to git integration, Sept 2026 (third party)",
      "url": "https://www.phdata.io/blog/sigma-api-to-git-integration/"
    },
    {
      "title": "Vendr: Sigma buyer pricing data (third party)",
      "url": "https://www.vendr.com/marketplace/sigma"
    },
    {
      "title": "Capterra UK: Sigma reviews (third party)",
      "url": "https://www.capterra.co.uk/software/188405/sigma"
    }
  ],
  "lastReviewed": "2026-11-03"
};
